import { Id } from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { produce } from 'immer'
import { performAction } from 'shared/queries/perform_action'
import keys from './shared/keys'
import { NotificationsData, NotificationsPage } from './shared/types'

type Variables = {
  notifications: NotificationsPage
  storeId?: Id
}

export function useMarkAllNotificationsRead() {
  const { server } = useServer()
  const client = useQueryClient()

  return useMutation({
    mutationFn: updateNotifications,
    onSuccess: async (_, { storeId }: Variables) => {
      const key = storeId ? keys.list(storeId) : keys.all
      const data = client.getQueryData<NotificationsData>(key)

      if (!data) {
        return
      }

      await client.cancelQueries({ queryKey: key })
      client.setQueryData<NotificationsData>(
        key,
        produce(data, updatedData => {
          updatedData.pages.forEach(page => {
            page.notifications.forEach(notification => {
              notification.read = true
            })
          })
        })
      )
    },
    onSettled: async _ => {
      await client.invalidateQueries({ queryKey: keys.summary() })
    },
  })

  async function updateNotifications({
    notifications,
  }: Variables): Promise<void> {
    await performAction(server, notifications.actions?.markAllAsRead)
  }
}
